import React, { useEffect, useState, useCallback } from "react";
import { connect } from "react-redux";
import HttpService from "../../services/httpService";
import { setSnackInfo } from "../../redux/actions/appAction";
import {
    Grid,
    Paper,
    Card,
    CardContent,
    FormControl,
    TextField,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TableContainer,
    TablePagination,
    Dialog,
    DialogTitle,
    DialogContent,
    Chip,
    CircularProgress,
    withStyles,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import Autocomplete from "@material-ui/lab/Autocomplete";
import CloseIcon from "@material-ui/icons/Close";
import TuneIcon from "@material-ui/icons/Tune";
import SearchIcon from "@material-ui/icons/Search";
import ClearIcon from "@material-ui/icons/Clear";
import TrendingUpIcon from "@material-ui/icons/TrendingUp";
import PeopleAltIcon from "@material-ui/icons/PeopleAlt";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import HourglassEmptyIcon from "@material-ui/icons/HourglassEmpty";
import GetAppIcon from "@material-ui/icons/GetApp";
import IconButton from "@material-ui/core/IconButton";
import { Button } from "antd";
import moment from "moment";
import XLSX from "xlsx";

const GOOD = "#0ca30c";
const WARNING = "#fab219";
const CRITICAL = "#d03b3b";
const BLUE = "#2a78d6";
const BORDER = "1px solid #d9dde3";

const cellBorder = { border: BORDER };

const StyledHeadCell = withStyles(() => ({
    root: {
        backgroundColor: "#1C375D",
        color: "#FFF2EB",
        fontWeight: 500,
        whiteSpace: "nowrap",
        border: BORDER,
        borderColor: "#1C375D",
    },
}))(TableCell);

const StyledBodyRow = withStyles(() => ({
    root: {
        transition: "background-color 0.15s ease",
        "&:hover": {
            backgroundColor: "#F5F7FB",
        },
        "&:nth-of-type(even)": {
            backgroundColor: "#FAFBFD",
        },
    },
}))(TableRow);

const STAGES = ["Real", "Junk", "Pending"];
const STAGE_COLOR = { Real: GOOD, Junk: CRITICAL, Pending: WARNING };

const EMPTY_FILTERS = { startDate: "", endDate: "", city: [], experienceCenterId: [], stage: null };
const EMPTY_DRILLDOWN = { open: false, rows: [], count: 0, page: 0, limit: 200, context: null };

const stageOfLeadType = (leadType) => {
    if (leadType === "Real") return "Real";
    if (leadType === "Junk") return "Junk";
    if ((leadType || "").includes("AI Verification Pending")) return "Pending";
    return leadType || "-";
};

const useStyles = makeStyles(() => ({
    filterPaper: {
        padding: 20,
        marginBottom: 20,
        borderRadius: 14,
        boxShadow: "0 2px 14px rgba(28,55,93,0.08)",
    },
    filterHeader: {
        display: "flex",
        alignItems: "center",
        gap: 8,
        marginBottom: 18,
        color: "#1C375D",
        fontWeight: 600,
        fontSize: 15,
    },
    filterRow: {
        display: "flex",
        alignItems: "flex-end",
        flexWrap: "nowrap",
        gap: 16,
        overflowX: "auto",
        paddingBottom: 4,
    },
    statCard: {
        borderRadius: 14,
        cursor: "pointer",
        transition: "transform 0.18s ease, box-shadow 0.18s ease",
        "&:hover": {
            transform: "translateY(-4px)",
            boxShadow: "0 10px 24px rgba(28,55,93,0.14)",
        },
    },
    tableCard: {
        borderRadius: 14,
        boxShadow: "0 2px 14px rgba(28,55,93,0.08)",
    },
    tableHeaderRow: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexWrap: "wrap",
        gap: 8,
        marginBottom: 10,
    },
    tableTitle: {
        display: "flex",
        alignItems: "center",
        gap: 8,
        color: "#1C375D",
        fontWeight: 600,
        fontSize: 16,
    },
    applyButton: {
        borderRadius: "20px !important",
        fontWeight: 600,
        paddingLeft: "18px !important",
        paddingRight: "18px !important",
        display: "inline-flex !important",
        alignItems: "center !important",
        boxShadow: "0 4px 10px rgba(42,120,214,0.32) !important",
        transition: "transform 0.15s ease, box-shadow 0.15s ease !important",
        "&:hover": {
            transform: "translateY(-1px)",
            boxShadow: "0 6px 14px rgba(42,120,214,0.4) !important",
        },
    },
    clearButton: {
        borderRadius: "20px !important",
        fontWeight: 500,
        paddingLeft: "18px !important",
        paddingRight: "18px !important",
        display: "inline-flex !important",
        alignItems: "center !important",
    },
}));

const StatTile = ({ label, value, accent, icon, onClick, loading }) => {
    const classes = useStyles();
    return (
        <Card
            className={classes.statCard}
            style={{ borderTop: `3px solid ${accent}`, height: "100%" }}
            onClick={onClick}
        >
            <CardContent style={{ display: "flex", alignItems: "center", gap: 14 }}>
                <div
                    style={{
                        width: 44,
                        height: 44,
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: `${accent}1a`,
                        color: accent,
                        flexShrink: 0,
                    }}
                >
                    {icon}
                </div>
                <div>
                    <div style={{ fontSize: 13, color: "#716ACA", fontWeight: 500 }}>{label}</div>
                    {loading ? (
                        <CircularProgress size={22} thickness={5} style={{ color: accent, marginTop: 6 }} />
                    ) : (
                        <div style={{ fontSize: 28, fontWeight: 600, color: "#1C375D", marginTop: 2 }}>{value ?? "-"}</div>
                    )}
                </div>
            </CardContent>
        </Card>
    );
};

const AiCallingAnalyticsDashboard = (props) => {
    const classes = useStyles();
    const [filters, setFilters] = useState(EMPTY_FILTERS);
    const [allExperienceCenters, setAllExperienceCenters] = useState([]);
    const [experienceCenters, setExperienceCenters] = useState([]);
    const [cities, setCities] = useState([]);
    const [summary, setSummary] = useState(null);
    const [summaryLoading, setSummaryLoading] = useState(false);
    const [drilldown, setDrilldown] = useState(EMPTY_DRILLDOWN);
    const [drilldownLoading, setDrilldownLoading] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [hoveredCell, setHoveredCell] = useState(null);

    const fetchFilterOptions = useCallback(async () => {
        try {
            const response = await HttpService.getAiCallingAnalyticsFilters();
            setAllExperienceCenters(response.data.experienceCenters);
            setExperienceCenters(response.data.experienceCenters);
        } catch (error) {
            props.setSnackInfo(error?.response?.data?.error ?? "Failed to load filter options", "error");
        }

        try {
            const locationResponse = await HttpService.getLocationList();
            setCities(locationResponse?.data || []);
        } catch (error) {
            props.setSnackInfo(error?.response?.data?.error ?? "Failed to load city list", "error");
        }
    }, [props]);

    const handleCityChange = async (e, selectedCities) => {
        setFilters({ ...filters, city: selectedCities, experienceCenterId: [] });

        if (!selectedCities.length) {
            setExperienceCenters(allExperienceCenters);
            return;
        }

        try {
            const responses = await Promise.all(
                selectedCities.map((city) => HttpService.getExperienceCentersByCity(city._id))
            );
            const merged = responses.flatMap((res) => res.data || []);
            const deduped = Array.from(new Map(merged.map((ec) => [ec._id, ec])).values());
            setExperienceCenters(deduped);
        } catch (error) {
            props.setSnackInfo(error?.response?.data?.error ?? "Failed to load experience centers for the selected city", "error");
        }
    };

    const fetchSummary = useCallback(async () => {
        setSummaryLoading(true);
        try {
            const params = {};
            if (filters.startDate) params.startDate = filters.startDate;
            if (filters.endDate) params.endDate = filters.endDate;
            if (filters.city.length) params.city = filters.city.map((x) => x._id).join(",");
            if (filters.experienceCenterId.length) params.experienceCenterId = filters.experienceCenterId.map((x) => x._id).join(",");
            if (filters.stage) params.stage = filters.stage;

            const response = await HttpService.getAiCallingAnalyticsSummary(params);
            setSummary(response.data);
        } catch (error) {
            props.setSnackInfo(error?.response?.data?.error ?? "Failed to load AI calling analytics", "error");
        }
        setSummaryLoading(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters, props]);

    useEffect(() => {
        fetchFilterOptions();
        fetchSummary();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const clearFilters = () => {
        setFilters(EMPTY_FILTERS);
        setExperienceCenters(allExperienceCenters);
    };

    const kpis = summary?.kpis;
    const pivot = summary?.pivot;

    const ecIdByCode = experienceCenters.reduce((acc, ec) => {
        acc[ec.code || ec.name] = ec._id;
        return acc;
    }, {});
    // Leads with no experience center at all land in this column - there's no
    // real _id for it, so a dedicated sentinel tells the backend to match
    // "no EC" specifically instead of silently skipping the EC filter.
    ecIdByCode['Unassigned'] = 'unassigned';

    // context.stage overrides the global Stage filter (used by the KPI tiles,
    // which drill into one specific stage regardless of what's picked in the
    // filter bar). When omitted, existing per-cell drilldowns keep behaving
    // exactly as before by falling back to filters.stage.
    const buildDrilldownParams = (context, page, limit) => {
        const params = { page: page + 1, limit };
        if (context.monthKey) params.monthKey = context.monthKey;
        if (context.experienceCenterId) params.experienceCenterId = context.experienceCenterId;
        if (filters.city.length) params.city = filters.city.map((x) => x._id).join(",");
        if (filters.startDate) params.startDate = filters.startDate;
        if (filters.endDate) params.endDate = filters.endDate;
        const stage = context.stage !== undefined ? context.stage : filters.stage;
        if (stage) params.stage = stage;
        return params;
    };

    const loadDrilldownPage = async (context, page, limit) => {
        // Open the popup immediately (with whatever rows it already has, if
        // any) so the loader shows inside it right away instead of waiting
        // on the page-level loader.
        setDrilldown((prev) => ({ ...prev, open: true, context, page, limit }));
        setDrilldownLoading(true);
        try {
            const params = buildDrilldownParams(context, page, limit);
            const response = await HttpService.getAiCallingAnalyticsDrilldown(params);
            setDrilldown({ open: true, rows: response.data.rows, count: response.data.count, page, limit, context });
        } catch (error) {
            props.setSnackInfo(error?.response?.data?.error ?? "Failed to load leads", "error");
        }
        setDrilldownLoading(false);
    };

    const openDrilldown = (monthKey, ecCode) => {
        if (!pivot?.cells?.[monthKey]?.[ecCode]) return;
        const context = { monthKey, ecCode, experienceCenterId: ecIdByCode[ecCode] };
        loadDrilldownPage(context, 0, drilldown.limit);
    };

    const openKpiDrilldown = (stage, label) => {
        if (!kpis) return;
        const context = {
            monthKey: null,
            ecCode: null,
            label,
            stage,
            experienceCenterId: filters.experienceCenterId.length ? filters.experienceCenterId.map((x) => x._id).join(",") : null,
        };
        loadDrilldownPage(context, 0, drilldown.limit);
    };

    const closeDrilldown = () => setDrilldown(EMPTY_DRILLDOWN);

    const downloadDrilldownExcel = async () => {
        if (!drilldown.context) return;
        setExporting(true);
        try {
            const params = buildDrilldownParams(drilldown.context, 0, drilldown.count || 10000);
            const response = await HttpService.getAiCallingAnalyticsDrilldown(params);
            const rows = response.data.rows || [];

            if (!rows.length) {
                props.setSnackInfo("No data available to download", "info");
                return;
            }

            const data = rows.map((row, i) => ({
                "Sl. No.": i + 1,
                "Lead No.": row.leadNo ?? "-",
                "Client Name": row.customerName || "-",
                "Number": row.phone || "-",
                "EC Name": row.experienceCenterName || "-",
                "Status": row.leadStatus || "-",
                "AI Classification": stageOfLeadType(row.leadType),
                "Current Stage": row.currentStage || "-",
                "Project Needs": row.projectNeeds || "-",
                "Budget 3L+ Suits": row.threeLakhsSuits || "-",
                "Start Project By": row.startProjectBy || "-",
                "Created Date": row.createdDate ? moment(row.createdDate).format("DD-MM-YYYY") : "-",
            }));

            const ws = XLSX.utils.json_to_sheet(data);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "Leads");
            XLSX.writeFile(wb, `AI_Calling_Leads_${moment().format("YYYYMMDD_HHmmss")}.xlsx`);
        } catch (error) {
            props.setSnackInfo(error?.response?.data?.error ?? "Failed to download leads", "error");
        }
        setExporting(false);
    };

    return (
        <div className="App" style={{ padding: 16 }}>
            <h2 style={{ color: "#1C375D" }}>AI Calling Analytics — HoomanLabs</h2>

            <Paper className={classes.filterPaper}>
                <div className={classes.filterHeader}>
                    <TuneIcon fontSize="small" />
                    Filters
                </div>
                <div className={classes.filterRow}>
                    <TextField
                        label="Start Date"
                        type="date"
                        InputLabelProps={{ shrink: true }}
                        value={filters.startDate}
                        onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
                        style={{ minWidth: 150 }}
                    />
                    <TextField
                        label="End Date"
                        type="date"
                        InputLabelProps={{ shrink: true }}
                        value={filters.endDate}
                        onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
                        style={{ minWidth: 150 }}
                    />
                    <FormControl style={{ minWidth: 170 }}>
                        <Autocomplete
                            multiple
                            size="small"
                            value={filters.city}
                            onChange={handleCityChange}
                            options={cities}
                            getOptionLabel={(option) => option.name || ""}
                            renderInput={(params) => <TextField {...params} label="City" variant="outlined" />}
                        />
                    </FormControl>
                    <FormControl style={{ minWidth: 220 }}>
                        <Autocomplete
                            multiple
                            size="small"
                            value={filters.experienceCenterId}
                            onChange={(e, v) => setFilters({ ...filters, experienceCenterId: v })}
                            options={experienceCenters}
                            getOptionLabel={(option) => option.name || option.code || ""}
                            renderInput={(params) => <TextField {...params} label="Experience Center" variant="outlined" />}
                        />
                    </FormControl>
                    <FormControl style={{ minWidth: 170 }}>
                        <Autocomplete
                            size="small"
                            value={filters.stage}
                            onChange={(e, v) => setFilters({ ...filters, stage: v })}
                            options={STAGES}
                            renderInput={(params) => <TextField {...params} label="Stage" variant="outlined" />}
                        />
                    </FormControl>
                    <div style={{ display: "flex", gap: 8, flexShrink: 0, marginLeft: "auto" }}>
                        <Button
                            type="primary"
                            icon={<SearchIcon style={{ fontSize: 16, marginRight: 6 }} />}
                            onClick={fetchSummary}
                            className={classes.applyButton}
                        >
                            Apply
                        </Button>
                        <Button icon={<ClearIcon style={{ fontSize: 16, marginRight: 6 }} />} onClick={clearFilters} className={classes.clearButton}>
                            Clear
                        </Button>
                    </div>
                </div>
            </Paper>

            <Grid container spacing={2} style={{ marginBottom: 16 }}>
                <Grid item xs={6} sm={3}>
                    <StatTile label="Total" value={kpis?.totalLeads} accent={BLUE} icon={<PeopleAltIcon />} loading={summaryLoading} onClick={() => openKpiDrilldown(undefined, "Total Leads")} />
                </Grid>
                <Grid item xs={6} sm={3}>
                    <StatTile label="Total Real" value={kpis?.realLeads} accent={GOOD} icon={<CheckCircleIcon />} loading={summaryLoading} onClick={() => openKpiDrilldown("Real", "Real Leads")} />
                </Grid>
                <Grid item xs={6} sm={3}>
                    <StatTile label="Total Junk" value={kpis?.junkLeads} accent={CRITICAL} icon={<DeleteOutlineIcon />} loading={summaryLoading} onClick={() => openKpiDrilldown("Junk", "Junk Leads")} />
                </Grid>
                <Grid item xs={6} sm={3}>
                    <StatTile label="Total Pending" value={kpis?.pendingLeads} accent={WARNING} icon={<HourglassEmptyIcon />} loading={summaryLoading} onClick={() => openKpiDrilldown("Pending", "Pending Leads")} />
                </Grid>
            </Grid>

            <Card className={classes.tableCard}>
                <CardContent>
                    <div className={classes.tableHeaderRow}>
                        <div className={classes.tableTitle}>
                            <TrendingUpIcon fontSize="small" />
                            Monthly Lead Volume by Experience Center
                        </div>
                        <p style={{ color: "#716ACA", fontSize: 13, margin: 0 }}>Click any count to see the underlying leads.</p>
                    </div>
                    <TableContainer style={{ overflow: "auto", maxHeight: 480, border: BORDER, borderRadius: 4 }}>
                        <Table size="small" style={{ borderCollapse: "collapse" }}>
                            <TableHead>
                                <TableRow>
                                    <StyledHeadCell style={{ position: "sticky", top: 0, left: 0, zIndex: 3 }}>Month</StyledHeadCell>
                                    {(pivot?.ecCodes || []).map((ec) => (
                                        <StyledHeadCell key={ec} align="center" style={{ position: "sticky", top: 0, zIndex: 2 }}>
                                            {ec}
                                        </StyledHeadCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {(pivot?.months || []).map((monthKey, rowIdx) => (
                                    <StyledBodyRow key={monthKey} hover>
                                        <TableCell
                                            style={{
                                                fontWeight: 600,
                                                position: "sticky",
                                                left: 0,
                                                zIndex: 1,
                                                backgroundColor: rowIdx % 2 === 0 ? "#fff" : "#FAFBFD",
                                                ...cellBorder,
                                            }}
                                        >
                                            {moment(monthKey, "YYYY-MM").format("MMM YYYY")}
                                        </TableCell>
                                        {(pivot.ecCodes || []).map((ec) => {
                                            const value = pivot.cells[monthKey]?.[ec] || 0;
                                            const cellKey = `${monthKey}-${ec}`;
                                            const clickable = value > 0;
                                            return (
                                                <TableCell
                                                    key={cellKey}
                                                    align="center"
                                                    onClick={() => clickable && openDrilldown(monthKey, ec)}
                                                    onMouseEnter={() => clickable && setHoveredCell(cellKey)}
                                                    onMouseLeave={() => setHoveredCell(null)}
                                                    style={{
                                                        ...cellBorder,
                                                        cursor: clickable ? "pointer" : "default",
                                                        fontWeight: 600,
                                                        color: clickable ? BLUE : "#a0a4ab",
                                                        backgroundColor: hoveredCell === cellKey ? "#E9F1FC" : "transparent",
                                                        textDecoration: hoveredCell === cellKey ? "underline" : "none",
                                                    }}
                                                >
                                                    {value}
                                                </TableCell>
                                            );
                                        })}
                                    </StyledBodyRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </CardContent>
            </Card>

            <Dialog open={drilldown.open} onClose={closeDrilldown} fullWidth maxWidth="lg">
                <DialogTitle style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <span>
                        Leads — {drilldown.context?.ecCode || drilldown.context?.label}
                        {drilldown.context?.monthKey ? ` · ${moment(drilldown.context.monthKey, "YYYY-MM").format("MMM YYYY")}` : ""}
                    </span>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <Button
                            icon={!exporting && <GetAppIcon style={{ fontSize: 16, marginRight: 6 }} />}
                            loading={exporting}
                            onClick={downloadDrilldownExcel}
                            className={classes.applyButton}
                        >
                            {exporting ? "Downloading..." : "Download Excel"}
                        </Button>
                        <IconButton size="small" onClick={closeDrilldown}>
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </div>
                </DialogTitle>
                <DialogContent>
                    {drilldownLoading ? (
                        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: 300, gap: 12 }}>
                            <CircularProgress />
                            <span style={{ color: "#716ACA", fontSize: 13 }}>Loading leads...</span>
                        </div>
                    ) : (
                        <>
                            <TableContainer style={{ overflow: "auto", maxHeight: "55vh", border: BORDER, borderRadius: 4 }}>
                                <Table size="small" style={{ borderCollapse: "collapse" }}>
                                    <TableHead>
                                        <TableRow>
                                            <StyledHeadCell style={{ position: "sticky", top: 0, zIndex: 2 }}>Lead No.</StyledHeadCell>
                                            <StyledHeadCell style={{ position: "sticky", top: 0, zIndex: 2 }}>Client Name</StyledHeadCell>
                                            <StyledHeadCell style={{ position: "sticky", top: 0, zIndex: 2 }}>Number</StyledHeadCell>
                                            <StyledHeadCell style={{ position: "sticky", top: 0, zIndex: 2 }}>EC Name</StyledHeadCell>
                                            <StyledHeadCell style={{ position: "sticky", top: 0, zIndex: 2 }}>Status</StyledHeadCell>
                                            <StyledHeadCell style={{ position: "sticky", top: 0, zIndex: 2 }}>AI Classification</StyledHeadCell>
                                            <StyledHeadCell style={{ position: "sticky", top: 0, zIndex: 2 }}>Current Stage</StyledHeadCell>
                                            <StyledHeadCell style={{ position: "sticky", top: 0, zIndex: 2 }}>Project Needs</StyledHeadCell>
                                            <StyledHeadCell style={{ position: "sticky", top: 0, zIndex: 2 }}>Budget 3L+ Suits</StyledHeadCell>
                                            <StyledHeadCell style={{ position: "sticky", top: 0, zIndex: 2 }}>Start Project By</StyledHeadCell>
                                            <StyledHeadCell style={{ position: "sticky", top: 0, zIndex: 2 }}>Created Date</StyledHeadCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {drilldown.rows.map((row, idx) => (
                                            <StyledBodyRow key={row.leadNo ?? idx} hover>
                                                <TableCell style={cellBorder}>
                                                    {row.leadId ? (
                                                        <span
                                                            onClick={() => window.open(`/project-logs/${row.leadId}`, "_blank", "noopener,noreferrer")}
                                                            style={{ color: BLUE, cursor: "pointer", fontWeight: 600, textDecoration: "underline" }}
                                                        >
                                                            {row.leadNo ?? "-"}
                                                        </span>
                                                    ) : (
                                                        row.leadNo ?? "-"
                                                    )}
                                                </TableCell>
                                                <TableCell style={cellBorder}>{row.customerName || "-"}</TableCell>
                                                <TableCell style={cellBorder}>{row.phone || "-"}</TableCell>
                                                <TableCell style={cellBorder}>{row.experienceCenterName || "-"}</TableCell>
                                                <TableCell style={cellBorder}>{row.leadStatus || "-"}</TableCell>
                                                <TableCell style={cellBorder}>
                                                    <Chip
                                                        size="small"
                                                        label={stageOfLeadType(row.leadType)}
                                                        style={{
                                                            backgroundColor: `${STAGE_COLOR[stageOfLeadType(row.leadType)] || "#999"}1a`,
                                                            color: STAGE_COLOR[stageOfLeadType(row.leadType)] || "#555",
                                                            fontWeight: 600,
                                                        }}
                                                    />
                                                </TableCell>
                                                <TableCell style={cellBorder}>{row.currentStage || "-"}</TableCell>
                                                <TableCell style={cellBorder}>{row.projectNeeds || "-"}</TableCell>
                                                <TableCell style={cellBorder}>{row.threeLakhsSuits || "-"}</TableCell>
                                                <TableCell style={cellBorder}>{row.startProjectBy || "-"}</TableCell>
                                                <TableCell style={cellBorder}>{moment(row.createdDate).format("DD-MM-YYYY")}</TableCell>
                                            </StyledBodyRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            <TablePagination
                                component="div"
                                count={drilldown.count}
                                page={drilldown.page}
                                onChangePage={(e, newPage) => loadDrilldownPage(drilldown.context, newPage, drilldown.limit)}
                                rowsPerPage={drilldown.limit}
                                onChangeRowsPerPage={(e) => loadDrilldownPage(drilldown.context, 0, Number(e.target.value))}
                                rowsPerPageOptions={[200, 500, 750]}
                            />
                        </>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default connect(null, { setSnackInfo })(AiCallingAnalyticsDashboard);
